import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { useClientSelector } from 'client/reducers';
import { selectIsNodeSelected } from 'client/reducers/editor/selectors';
import css from './Toolbar.scss';

const SelectionToolbar = (props: { children: ReactNode }) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [pin, setPin] = useState<'top' | 'bottom' | 'topInset'>('top');

	// observe and auto-fix position of the toolbar
	useEffect(() => {
		let rafID = 0;
		let frameCounter = 0;
		function onRAF() {
			// re-check each 2sec (every 120 frame) (performance optimization)
			if (frameCounter % (60 * 2) === 0) {
				const el = ref.current;
				const parent = el?.parentElement;

				if (!el || !parent) return;

				const bbox = el.getBoundingClientRect();
				const pbbox = parent.getBoundingClientRect();

				// check Y
				if (pbbox.top >= bbox.height) {
					setPin('top');
				} else if (pbbox.bottom < window.innerHeight) {
					setPin('bottom');
				} else {
					setPin('topInset');
				}

				// check X
				const xEndOffset = window.innerWidth - (pbbox.left + bbox.width);
				if (pbbox.left < 0) {
					// shift to right
					el.style.transform = `translateX(${Math.abs(bbox.left)}px)`;
				} else if (xEndOffset < 0) {
					// shift to left
					el.style.transform = `translateX(${xEndOffset}px)`;
				} else {
					el.style.transform = '';
				}
			}

			frameCounter += 1;
			rafID = window.requestAnimationFrame(onRAF);
		}

		window.requestAnimationFrame(onRAF);

		return () => {
			window.cancelAnimationFrame(rafID);
		};
	}, []);

	return (
		<div className={cn(css.selectionToolbar, css[pin])} ref={ref}>
			{props.children}
			<div className={css.actions} id="selection-toolbar-actions" />
		</div>
	);
};

/*
 Use this component to render any content into a selection toolbar
 */
export const SelectionToolbarPortal = ({ children, nodeID }: { children: ReactNode; nodeID: string }) => {
	const [toolbar, setToolbar] = useState<HTMLElement | null>(null);
	const isSelected = useClientSelector(state => selectIsNodeSelected(state, nodeID));

	useEffect(() => {
		if (isSelected) {
			setToolbar(document.getElementById('selection-toolbar-actions'));
		}
	}, [isSelected, children]);

	return toolbar && children ? (
		<div
			style={{ position: 'absolute' }}
			// note: Why stopPropagation?
			// Need to prevent invocation of e.g. dbClick event which is may be fired
			// at building block, that renders some components into toolbar via SelectionToolbarPortal
			onClick={e => {
				e.stopPropagation();
			}}
			onDoubleClick={e => {
				e.stopPropagation(); // e.g. to stop dbClick on image which toggle an edit mode
			}}
			onFocus={e => {
				e.stopPropagation(); // stop onFocus event provided to building blocks by SelectionHintProvider
			}}
		>
			{createPortal(children, toolbar)}
		</div>
	) : null;
};

export default SelectionToolbar;
